<template>
  <div class="container" :class="{ 'only-mirror-list': !hasFilteredMirror }">
    <AppBarMirror class="details" app clipped-left height="88px">
      <template v-slot:title>
        <div v-if="isTabletView" class="title-mobile text-truncate">
          {{ currentProgramTag }}
        </div>
        <template v-else>
          {{ currentProgramTag }}
        </template>
      </template>
      <template v-slot:updatedDate v-if="dateChangedMirror">
        <span
          class="font-weight-regular"
          :class="loadingUpdateMirror ? 'mirror-updated' : ''"
          v-if="$vuetify.breakpoint.xsOnly"
        >
          Última atualização: <br />
          {{ dateChangedMirror }}
        </span>
        <span
          class="font-weight-regular"
          :class="loadingUpdateMirror ? 'mirror-updated' : ''"
          v-else
        >
          Última atualização: {{ dateChangedMirror }}
        </span>
      </template>

      <template v-if="$vuetify.breakpoint.xsOnly" v-slot:actions-right>
        <v-btn icon @click="handleAutoScrollClick()">
          <v-icon>schedule</v-icon>
        </v-btn>
        <v-btn
          icon
          @click="handleFocusClick()"
          :class="{
            disabled: disableFocusButton,
          }"
        >
          <v-icon>center_focus_weak</v-icon>
        </v-btn>
        <v-btn icon @click="loadMirror">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </template>
      <template v-if="$vuetify.breakpoint.smAndUp" v-slot:toggles>
        <v-tabs v-model="tab">
          <v-tab v-for="item in iconsTab" :key="item">
            <v-icon small>{{ item }}</v-icon>
          </v-tab>
        </v-tabs>
      </template>
    </AppBarMirror>

    <Error
      v-if="noFolderAccess"
      textTitle="Tão <span>vazio</span> por aqui... <br/>Onde estão os <span>espelhos</span>?!"
      textDescription="Não encontramos nenhuma gaveta de espelhos vinculada ao seu usuário no iNews."
      imageTitle="bored"
      class="no-folder-access"
    />

    <v-tabs-items v-model="tab" v-if="$vuetify.breakpoint.smAndUp">
      <v-tab-item class="card-vision">
        <!-- Complete View -->
        <BaseLayout
          :drawer="drawer"
          :mobile="breakpoint.smOnly"
          :width-drawer="breakpoint.mdOnly ? 300 : 400"
          @change-drawer="drawer = $event"
        >
          <!-- Navigation -->
          <template v-slot:navigation-drawer>
            <MirrorSidebar
              v-if="!loadingMirror"
              :selectedSlugId="selectedSlug.id"
              :selectedMirror="selectedMirror"
              :filteredMirror="filteredMirror"
              :disableFocusButton="disableFocusButton"
              @openMirrorItemDetails="handleMirrorItemDetails"
              @schedule-focus="handleAutoScrollClick"
              @focus="handleFocusClick"
              @refresh="loadMirror"
              @drawer-click="drawer = false"
            />
          </template>

          <!-- Main -->
          <template v-slot:main>
            <div
              v-touch="{
                left: () => handlerPagination('next'),
                right: () => handlerPagination('previous'),
              }"
              class="container-touch"
            >
              <div class="d-flex align-center">
                <v-btn
                  v-if="!drawer"
                  icon
                  @click="drawer = true"
                  class="button-drawer-menu mr-3"
                >
                  <v-icon color="primary">menu</v-icon>
                </v-btn>
                <MirrorNavigation
                  class="mb-3"
                  :slug="selectedSlug"
                  :mirror="filteredMirror"
                  @changeSelectedMirrorItem="selectedMirrorIdItem = $event"
                />
              </div>
              <MirrorDetails
                v-show="!loadingMirror"
                v-if="hasFilteredMirror"
                :slug="selectedSlug"
              />
            </div>
          </template>
        </BaseLayout>
      </v-tab-item>
      <v-tab-item class="table-vision">
        <div class="mirror-main-content">
          <MirrorTable
            :mirrorData="filteredMirror"
            @openMirrorDetail="openMirror($event)"
            :style="`height: ${heightTableMirror}`"
          />
          <div
            v-if="selectedMirrorItemTable"
            class="divisor-table-mirror"
            @mousedown="startDragging"
            @mousemove="moveDragging"
            @mouseup="stopDragging"
            @click="handleTabletViewResize"
          >
            <div class="button-center">
              <v-icon
                small
                :class="{
                  'first-icon': true,
                  'first-icon-tablet': isTabletView && !isShowingTable,
                }"
                >mdi-menu-up</v-icon
              >
              <v-icon
                small
                :class="{
                  'second-icon': true,
                  'second-icon-tablet': isTabletView && isShowingTable,
                }"
                >mdi-menu-down</v-icon
              >
            </div>
          </div>
          <div
            v-if="selectedMirrorItemTable"
            :style="`height: ${heightDetailMirror}`"
          >
            <MirrorDetails
              style="height: inherit;"
              v-if="selectedMirrorItemTable"
              :slug="selectedMirrorItemTable"
            />
          </div>
        </div>
      </v-tab-item>
    </v-tabs-items>

    <!-- Simple View -->
    <MirrorSidebar
      v-if="!loadingMirror && breakpoint.xsOnly"
      style="padding-top: 100px"
      :selectedSlugId="selectedSlug.id"
      :selectedMirror="selectedMirror"
      :filteredMirror="filteredMirror"
      :disableFocusButton="disableFocusButton"
      @openMirrorItemDetails="handleMirrorItemDetails"
      @schedule-focus="handleAutoScrollClick"
      @focus="handleFocusClick"
      @refresh="loadMirror"
      @drawer-click="drawer = false"
    />

    <LoadingOverlay :enabled="loadingMirror" />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import AppBarMirror from '@/components/mirrors/AppBarMirror.vue';
import BaseLayout from '../components/shared/BaseLayout.vue';
import Error from '@/components/shared/Error.vue';
import MirrorSidebar from '@/components/mirrors/MirrorSidebar.vue';
import { actions, categories } from '@/constants/analytics.js';
import LoadingOverlay from '@/components/shared/LoadingOverlay.vue';
import MirrorDetails from '@/components/mirrors/MirrorDetails/index.vue';
import MirrorNavigation from '@/components/mirrors/MirrorNavigation.vue';
import MirrorTable from '@/components/mirrors/MirrorTable.vue';
import mirrorNavigation from '@/mixins/mirrorNavigation.js';
import checkAndUpdateSelectedMirror from '../mixins/checkAndUpdateSelectedMirror.mixin';

export default {
  name: 'mirror',
  mixins: [mirrorNavigation, checkAndUpdateSelectedMirror],
  components: {
    BaseLayout,
    MirrorNavigation,
    AppBarMirror,
    Error,
    MirrorSidebar,
    LoadingOverlay,
    MirrorDetails,
    MirrorTable,
  },

  data() {
    return {
      tab: null,
      iconsTab: ['mdi-view-grid', 'mdi-view-sequential'],
      timeoutProp: null,
      selectedMirrorIdItem: '',
      selectedMirrorItemTable: null,
      drawer: null,
      isDragging: false,
      tableHeight: '98vh',
      heightTableMirror: null,
      heightDetailMirror: null,
      lastMouseY: 0,
      isShowingTable: true,
      isTabletView: this.$vuetify.breakpoint.mdAndDown,
    };
  },

  watch: {
    selectedMirrorItemTable() {
      if (this.selectedMirrorItemTable) {
        this.tableHeight = '35vh';
        this.heightTableMirror = this.tableHeight;
        this.heightDetailMirror = '48vh';
      } else {
        this.tableHeight = '80vh';
      }
    },
  },

  computed: {
    ...mapGetters('mirror', ['userHasActivity', 'filteredMirror']),
    ...mapState({
      currentUser: state => state.user.currentUser,
      loadingUpdateMirror: state => state.mirror.loadingUpdateMirror,
      noFolderAccess: state => state.news.noFolderAccess,
      loadingMirror: state => state.mirror.loadingMirror,
      lastMirrorAcessed: state => state.mirror.lastMirrorAcessed,
      dateChangedMirror: state => state.mirror.dateChangedMirror,
      selectedMirror: state => state.mirror.selectedMirror,
    }),
    disableFocusButton() {
      return !this.userHasActivity(
        this.filteredMirror,
        this.currentUser.username,
      );
    },
    breakpoint() {
      return this.$vuetify.breakpoint;
    },

    selectedSlugId() {
      if (this.selectedMirrorIdItem) return this.selectedMirrorIdItem;
      if (this.lastMirrorAcessed) {
        const id = this.lastMirrorAcessed.replace('mirror-', '');
        if (this.hasStoryId(id)) return id;
      }
      if (this.hasFilteredMirror) return this.filteredMirror[0].id || 0;
      return 0;
    },
    // Usado por mixin mirrorNavigation
    slug() {
      return this.selectedSlug;
    },

    selectedSlug() {
      const slugId = this.selectedSlugId;
      if (!slugId) return {};
      const slugs = this.filteredMirror || [];
      const currentSlug = slugs.find(slug => slug.id === slugId);

      if (currentSlug) return currentSlug;
      return this.filteredMirror[0] || {};
    },

    hasFilteredMirror() {
      return Array.isArray(this.filteredMirror) && this.filteredMirror.length;
    },
    currentPlace() {
      return this.$route.params.place;
    },

    currentProgramTag() {
      return this.$route.params.program_tag;
    },
  },

  created() {
    this.loadMirrorWhenCreated();
    this.redirectNewFront();
  },

  methods: {
    handleTabletViewResize() {
      if (this.isTabletView) {
        if (this.isShowingTable) this.handleShowSlug();
        else this.handleShowTable();
      }
    },
    handleShowSlug() {
      this.heightTableMirror = `10vh`;
      this.heightDetailMirror = `90vh`;
      this.isShowingTable = false;
    },
    handleShowTable() {
      this.heightTableMirror = `35vh`;
      this.heightDetailMirror = `48vh`;
      this.isShowingTable = true;
    },
    handleClickChangeNotification() {
      this.$store.dispatch('mirror/setNotificationChangeMirror');
    },
    openMirror(item) {
      this.selectedMirrorItemTable = item;
      this.isShowingTable = true;
    },
    startDragging(event) {
      this.isDragging = true;
      if (this.lastMouseY === 0) this.lastMouseY = event.clientY;
    },
    moveDragging(event) {
      this.handleMouseMove(event);
    },
    stopDragging() {
      this.isDragging = false;
    },
    handleMouseMove(event) {
      if (this.isDragging) {
        const currentMouseY = event.clientY;
        let deltaY = currentMouseY - this.lastMouseY;
        const defaultHeightDetailMirror = '48vh';

        if (deltaY <= 0) {
          this.heightTableMirror = `calc(${this.tableHeight} - ${Math.abs(
            deltaY,
          )}px)`;

          this.heightDetailMirror = `calc(${defaultHeightDetailMirror} + ${Math.abs(
            deltaY,
          )}px)`;
        }

        if (deltaY < -350) {
          deltaY = -350;
          this.isDragging = false;
        }

        if (deltaY > 0) {
          deltaY = 0;
          this.isDragging = false;
        }
      }
    },
    // Usado por mixin mirrorNavigation
    changeSelectedMirrorItem(type) {
      this.selectedMirrorIdItem = this.getNextOrPreviousMirrorId(type);
    },

    hasStoryId(id) {
      return this.filteredMirror.some(mirror => mirror.id === id);
    },

    loadMirrorWhenCreated() {
      if (
        this.breakpoint.xsOnly &&
        this.filteredMirror.length &&
        this.filteredMirror[0].program_tag === this.$route.params.program_tag &&
        this.filteredMirror[0].place === this.$route.params.place
      )
        return null;
      this.loadMirror();
    },

    loadMirror() {
      this.$store.dispatch('mirror/selectMirror', {
        programName: this.$route.params.program_tag,
        place: this.$route.params.place,
      });
    },

    handleFocusClick() {
      this.$store.dispatch('analyticsEvent', {
        category: categories.mirror,
        action: actions.focusClicked,
        label: `${this.currentPlace}-${this.currentProgramTag}`,
      });
      this.$store.dispatch(
        'mirror/getReporterAutoScrollTarget',
        this.currentUser.username,
        this.filteredMirror,
      );
    },

    handleAutoScrollClick() {
      this.$store.dispatch('analyticsEvent', {
        category: categories.mirror,
        action: actions.autoScrollClicked,
        label: `${this.currentPlace}-${this.currentProgramTag}`,
      });
      this.$store.dispatch(
        'mirror/getTimeAutoScrollTarget',
        this.filteredMirror,
      );
    },

    handleMirrorItemDetails(activity) {
      if (this.$vuetify.breakpoint.smAndUp) {
        this.selectedMirrorIdItem = activity.id;
      } else
        this.$router.push({
          path: `/mirror/detail/${activity.place}/${activity.program_tag}`,
          query: { selectedSlugId: activity.id },
        });
    },

    redirectNewFront() {
      const { place, program_tag } = this.$route.params;
      window.location = `${process.env.VUE_APP_URL_NEW_NH}/mirrors/${place}/${program_tag}`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/theme-v2.scss';
@import '@/assets/scss/buttons.scss';
@import '@/assets/scss/fixes.scss';

.mirror-main-content {
  @include for-size(desktop-down) {
    margin-top: 96px;
    padding: 16px;
  }

  padding: 88px 16px 0;

  margin-left: 16px;
  margin-right: 16px;

  .divisor-table-mirror {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 35px;

    cursor: ns-resize;

    position: relative;

    .button-center {
      width: 25px;
      height: 25px;
      background-color: #ccc;
      z-index: 1;
      border-radius: 50%;
      transform: translateX(-50%);

      & i {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);

        &::after {
          display: none;
        }
      }

      & .first-icon {
        bottom: 8px;
      }

      & .first-icon-tablet {
        display: none;
      }
      & .second-icon {
        top: 8px;
      }

      & .second-icon-tablet {
        display: none;
      }
    }

    &::after {
      content: '';
      width: 100%;
      position: absolute;
      border-bottom: 1px solid #d9d9d9;
      left: 0;
      top: 50%;
    }
  }
}

::v-deep .theme--light.v-tabs-items {
  background-color: #f5f5f5;
}

::v-deep .container-touch {
  min-height: calc(100vh - 98px);
}
.container {
  background-color: $color-background;

  @include for-size(tablet-landscape-down) {
    overflow-y: hidden;
  }
}
.button-drawer-menu {
  background: $color-white;
}

.nh-alert-text {
  font-size: 14px !important;
  margin-right: 10px;
}

.title-mobile {
  width: 40vw !important;
}

::v-deep .table-wrapper {
  .v-data-table.mirror-table {
    .v-data-table__wrapper {
      height: inherit;
    }
  }
}
.mirror-updated {
  animation: notify-updated-mirror 1s infinite linear;
}

@keyframes notify-updated-mirror {
  0% {
    font-size: 10px;
    color: #707070;
  }

  50% {
    font-size: 12px;
    color: #fa5e15;
  }
}
</style>
